import {
  Box,
  CenteredLoader,
  Flex,
  Gauge,
  GaugeProps,
  InfoIconWithTooltip,
  Separator,
  SubTitle,
  Text,
  TooltipItem,
  useTheme,
} from "@shared/primitive-components"
import { useMemo } from "react"
import { FleetScore, SeverityLabel } from "."
import { noDataThreshold, threshold } from "../../../utils"
import { fleetSafetyThreshold } from "../../const"
import { gaugeStyles as styles } from "../../styles"

export type FleetGaugeProps = {
  title: string
  tooltip: string
  score: number | null
  loading?: boolean
}

const min = 0
const max = 100
const segments = 50

export const FLEET_GAUGE_DTI = {
  GAUGE_TITLE: `gauge-title`,
}

export const GAUGE_DTI = {
  component: `gauge-component`,
  maxLabel: `gauge-min-label`,
  minLabel: `gauge-max-label`,
}

export const FleetGauge = ({ title, tooltip, score, loading }: FleetGaugeProps) => {
  const theme = useTheme()

  const chart = useMemo<GaugeProps<number, string>>(() => {
    const points = [...Array(segments).keys()]

    const data = points.map((p) => {
      const start = (p + 1) * (max / segments) - max / segments
      const end = (p + 1) * (max / segments)

      const { color, label, labelColor, type } =
        score !== null ? threshold(start, end, score, theme) : noDataThreshold(start, theme)
      return {
        color,
        type,
        label,
        labelColor,
        value: 1,
      }
    })

    const toolTipTitleFn = (tooltipItem: TooltipItem<"doughnut">[]) => {
      const { dataIndex } = tooltipItem[0]
      const { type } = data[dataIndex]
      if (type === `low`) return `${fleetSafetyThreshold.low}+`
      if (type === `medium`) return `${fleetSafetyThreshold.medium} - ${fleetSafetyThreshold.low - 1}`
      if (type === `high`) return `${fleetSafetyThreshold.high} - ${fleetSafetyThreshold.medium - 1}`
      if (type === `severe`) return `0 - ${fleetSafetyThreshold.high - 1}`
      return ``
    }
    const toolTipLabelFn = (tooltipItem: TooltipItem<"doughnut">) => {
      const { dataIndex } = tooltipItem
      const { label } = data[dataIndex]
      return label ? `${label} Risk`.toUpperCase() : ``
    }
    const toolTipLabelColorFn = (tooltipItem: TooltipItem<"doughnut">) => {
      const { dataIndex } = tooltipItem
      const { labelColor } = data[dataIndex]
      return labelColor
    }

    return {
      showTooltip: score !== null,
      toolTipTitleFn,
      toolTipLabelFn,
      toolTipLabelColorFn,
      data,
    }
  }, [theme, score])

  const titleTooltipContent = <Text css={styles.tooltipContent}>{tooltip}</Text>

  return (
    <>
      <Flex alignItems="center">
        <SubTitle data-testid={FLEET_GAUGE_DTI.GAUGE_TITLE}>{title}</SubTitle>
        {tooltip && <InfoIconWithTooltip content={titleTooltipContent} />}
      </Flex>
      <Separator size="block" color="yellow" css={styles.separator} />

      {loading ? (
        <CenteredLoader top="8rem" />
      ) : (
        <>
          <Flex alignItems="center" justifyContent="center">
            <Box css={styles.gauge}>
              <Gauge chart={chart} dataTestIds={GAUGE_DTI} value={score} min={min} max={max} />
              <Box css={styles.scoreContainer}>
                <Box css={styles.marginBottom}>
                  <FleetScore score={score} />
                </Box>
                <SeverityLabel score={score} />
              </Box>
            </Box>
          </Flex>
          {!loading && score === null && (
            <Box>
              <Text css={styles.noData}>
                Check back once you have one month of data to see your current Safety Score and what you can do to
                improve.
              </Text>
            </Box>
          )}
        </>
      )}
    </>
  )
}

FleetGauge.defaultProps = {
  loading: undefined,
}
